import { Euler, Object3D, Vector3 } from "three";
import { Context } from "../../context";
import {
  DefensivePlayState,
  OffensivePlayMode,
  OffensivePlayState,
  PlayStateManagerCb,
} from "../../managers/playstate";
import { ControllerStrategy, CameraModeOptions } from "../strategy/strategy";
import {
  CameraLandmark,
  CameraLandmarkLookup,
  CameraMode,
  SmoothTimes,
} from "../controller";
import { Strikezone, StrikezonePersistent } from "../../world/strikezone";
import { PoserPlayerType } from "../../../lib/misc/player-type";
import { LookAtFriendlyTransitionControllerStrategy } from "../strategy/tracking/look-at/transition";

const CLIPPING_PLANE = 2.5;
const ACTION_RADIUS = 30.0;
const pitcherNameOverride = {
  pos: new Vector3(0, 11, -105),
  rot: new Euler(0, 0, 0),
  scale: 1.0,
};
const batterLeftOverride = {
  pos: new Vector3(1.25, 2, 5),
  rot: new Euler(0, 0, 0),
  scale: 0.15,
};
const batterRightOverride = {
  pos: new Vector3(-1.25, 2, 5),
  rot: new Euler(0, 0, 0),
  scale: 0.15,
};

export class StrikezoneControllerStrategy extends LookAtFriendlyTransitionControllerStrategy {
  private initialised = false;
  private contextRef?: Context;

  private prevZoomMin: number = 0;
  private prevZoomMax: number = Infinity;
  private prevZoom: number = 1;
  private prevAutoTrack: boolean = false;
  private prevDamping: number = SmoothTimes.MEDIUM;
  private prevNameplates: Set<number> = new Set();

  private transDuration = 1; //s

  private homePos: CameraLandmark = CameraLandmark.STRIKEZONE_CENTER;
  private midPitch = new Object3D();
  private zoomLevel: number = 1.0;

  private prevCamDist: number = 30;

  private stateUpdateCb?: PlayStateManagerCb;

  // TODO: centralise this into own strategy (from PAIR)
  private timeout?: any;
  private zoomTimeoutMs = 3000;
  private wheelEventOptions: AddEventListenerOptions = { passive: true };
  private mouseZoomCb = (e: WheelEvent) => {
    if (this.timeout !== undefined) {
      clearTimeout(this.timeout);
    }
    this.timeout = setTimeout(() => {
      this.registered?.controls?.zoomTo(this.zoomLevel, true);
    }, this.zoomTimeoutMs);
  };

  init(context: Context, _options: CameraModeOptions): ControllerStrategy {
    super.init(context);

    this.contextRef = context;

    if (!this.registered) {
      console.error("no controller registered for strikezone camera strategy");
      return this;
    }

    this.prevZoomMin = this.registered.state.zoomConfig.fov.min;
    this.prevZoomMax = this.registered.state.zoomConfig.fov.max;
    this.registered.state.zoomConfig.fov.max = 3;

    this.prevCamDist = this.registered.state.camDist;
    this.registered.state.camDist = 17.5;

    this.midPitch.name = "sz-mid-pitch";

    this.contextRef = context;

    this.midPitch.position.set(0, 0, -30);

    if (this.registered.controls) {
      this.prevDamping = this.registered.controls.smoothTime;
      this.prevZoom = this.registered.controls.camera.zoom;
    }
    this.prevAutoTrack = this.registered.state.autoTrack;
    this.prevNameplates = context.state.nameplatePositions;
    context.state.nameplatePositions = new Set([1, 10]);

    this.registered.domRef.addEventListener(
      "wheel",
      this.mouseZoomCb,
      this.wheelEventOptions
    );

    return this;
  }

  update(deltaS: number, gameTimeDelta: number, frameNum?: number) {
    super.update(deltaS, gameTimeDelta, frameNum);
    if (!this.registered?.controls) {
      console.error("no controls registered");
      return;
    }

    const { contextRef: context } = this;

    // lazy register for play state updates here because if we do it in init, the callback
    // triggering steps on itself (setMode gets called simultaneously in different threads)
    if (!this.initialised) {
      // play state callback
      this.stateUpdateCb = (
        defense: DefensivePlayState,
        offense: OffensivePlayState
      ) => {
        this.playStateUpdated(offense, defense);
      };

      this.registered.state.tracked = this.midPitch;

      const width = context?.container?.clientWidth;
      const height = context?.container?.clientHeight;

      // mobile position different
      if (height && width && height > width) {
        this.homePos = CameraLandmark.STRIKEZONE_CENTER_MOBILE;
        this.registered.state.zoomConfig.fov.min = 1.0;
        this.zoomLevel = 2.0;
        this.registered.controls?.zoomTo(this.zoomLevel, true);
        this.midPitch.position.y += 2.0;
      }

      this.registered.setMode(CameraMode.LOOK_AT_SIMPLE, { fromManager: true });
      this.setupTransition(
        CameraLandmarkLookup[this.homePos],
        this.transDuration
      );

      const playStateManager = context?.playStateManager;
      playStateManager?.addSubscriber("strikezone-cam", this.stateUpdateCb);

      if (context?.world) {
        context.world.strikezone.removeFromParent();
        context.world.strikezone = new StrikezonePersistent(context);
        context.world.add(context.world.strikezone);

        context.world.ball.ball.scale.setScalar(1);
      }

      this.initialised = true;
    }

    let actors = context?.world?.actors.getActors();
    if (actors) {
      for (let actor of actors) {
        let isBatter = actor.details.type == PoserPlayerType.BATTER;
        let isCatcher = actor.details.type == PoserPlayerType.CATCHER;
        let isPitcher = actor.details.type == PoserPlayerType.PITCHER;

        actor.hideBehindPlate();

        if (actor.nameplate) {
          if (isPitcher) {
            actor.nameplate.override = pitcherNameOverride;
          } else if (isBatter) {
            actor.nameplate.override =
              context?.gameState.getBatHand() == "Right"
                ? batterRightOverride
                : batterLeftOverride;
          } else {
            actor.nameplate.override = undefined;
          }
        }
        const ballPosition = context?.world?.ball.ball?.position;
        if (!isPitcher && !isBatter && !isCatcher && ballPosition) {
          if (
            actor.centroid.position.distanceTo(ballPosition) < ACTION_RADIUS
          ) {
            context?.state.nameplatePositions.add(actor.positionId);
          } else {
            context?.state.nameplatePositions.delete(actor.positionId);
          }
        }
      }
    }
  }

  playStateUpdated(offense: OffensivePlayState, defense: DefensivePlayState) {
    const controller = this.registered;
    if (!controller || !controller.controls) return;

    let lookAtBall = false;

    switch (offense.mode) {
      case OffensivePlayMode.IDLE:
        controller.controls.smoothTime = SmoothTimes.VERY_SLOW;
        break;

      case OffensivePlayMode.BATTING:
        controller.controls.smoothTime = SmoothTimes.FAST;
        break;

      case OffensivePlayMode.RUNNING: {
        controller.controls.smoothTime = SmoothTimes.VERY_FAST;
        lookAtBall = true;
        break;
      }

      default:
        console.error("unsupported game state in strikezone cam");
        break;
    }

    if (lookAtBall) {
      controller.state.tracked = this.contextRef?.world?.ball.ball;
    } else {
      controller.state.tracked = this.midPitch;
    }
  }

  teardown(): void {
    super.teardown();

    if (!this.registered) return;

    const { contextRef, controls, state } = this.registered;

    if (controls) {
      controls.smoothTime = this.prevDamping;
      controls.zoomTo(this.prevZoom, true);
    }

    this.registered.state.zoomConfig.fov.min = this.prevZoomMin;
    this.registered.state.zoomConfig.fov.max = this.prevZoomMax;
    state.autoTrack = this.prevAutoTrack;
    contextRef.state.nameplatePositions = this.prevNameplates;
    state.camDist = this.prevCamDist;

    const playStateManager = contextRef.playStateManager;
    playStateManager?.removeSubscriber("strikezone-cam");
    let actors = contextRef.world?.actors.getActors();
    if (actors) {
      for (let actor of actors) {
        actor.hide = false;
        if (actor.nameplate) {
          actor.nameplate.override = undefined;
        }
      }
    }

    if (contextRef.world) {
      contextRef.world.strikezone.removeFromParent();
      contextRef.world.strikezone = new Strikezone(contextRef);
      contextRef.world.add(contextRef.world.strikezone);
      contextRef.world?.ball.ball.scale.setScalar(contextRef.state.ballScale);
    }

    this.registered.domRef.removeEventListener(
      "wheel",
      this.mouseZoomCb,
      this.wheelEventOptions
    );
  }
}
